import { isEqual } from 'lodash'
import debounce from 'lodash.debounce'
import { useEffect, useRef, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { fetchGrandTotal, setCheckoutLoading } from './checkoutFunctions'

const useRecalculateGrandTotal = () => {
  const dispatch = useDispatch()
  const lastFetchedValues = useRef(null)
  const activeFetchId = useRef(null)

  const subtotal = useSelector(state => state.checkout.subtotal)
  const cartItems = useSelector(state => state.cartItems)
  const checkout = useSelector(state => state.checkout)
  const {
    taxQuote,
    shippingQuote,
    creditsUsed,
    appliedPromotions,
    appliedCoupon,
    subtotalLoading,
    subtotalCalculated
  } = checkout

  const fetchTotal = useCallback(
    values => {
      const debouncedFetch = debounce(vals => {
        const fetchId = Symbol()
        activeFetchId.current = fetchId

        dispatch(setCheckoutLoading(true))
        dispatch(
          fetchGrandTotal(
            vals.subtotal,
            vals.taxQuote,
            vals.shippingQuote,
            vals.creditsUsed,
            vals.appliedPromotions,
            vals.appliedCoupon,
            vals.cartItems
          )
        ).finally(() => {
          if (activeFetchId.current === fetchId) {
            dispatch(setCheckoutLoading(false))
          }
        })
      }, 800)

      debouncedFetch(values)
    },
    [dispatch]
  )

  useEffect(() => {
    // Wait for subtotal calculation to complete before fetching grand total
    if (subtotalLoading || !subtotalCalculated) {
      return
    }

    const currentValues = {
      subtotal,
      taxQuote,
      shippingQuote,
      creditsUsed,
      appliedPromotions,
      appliedCoupon,
      cartItems
    }

    // Use deep comparison to prevent unnecessary API calls for object dependencies
    if (lastFetchedValues.current && isEqual(currentValues, lastFetchedValues.current)) {
      return
    }

    lastFetchedValues.current = currentValues
    fetchTotal(currentValues)
  }, [
    subtotal,
    taxQuote,
    shippingQuote,
    creditsUsed,
    appliedPromotions,
    appliedCoupon,
    cartItems,
    subtotalLoading,
    subtotalCalculated,
    fetchTotal
  ])
}

export default useRecalculateGrandTotal
